<template>
	<div class="w-full">
		<!-- Manage ACH Modal -->
		<div v-if="accounts.length > 0 && store?.user?.id" class="w-full flex justify-end items-center mb-2 pr-4">
			<button @click="manageACH = true" class="text-ihh-light-blue font-normal text-xs">Manage</button>
		</div>
		<div class="relative w-full">
			<!-- Selected ACH Account Display -->
			<div
				v-if="accounts.length"
				@click.stop="toggleACHDropdown"
				class="w-full border bg-ihh-white mb-0 rounded h-auto p-2 text-sm cursor-pointer"
				:class="showACHDropdown ? 'rounded-b-none border-b-0' : ''"
			>
				<div class="flex justify-between items-center">
					<div v-if="!addingNewAccount" class="flex flex-col sm:flex-row sm:space-x-3 sm:items-center w-full">
						<div class="flex items-center space-x-2">
							<img :src="selectedAccount?.imageUrl" class="w-10 h-auto" />
						</div>
						<div class="flex flex-col text-xs font-medium mt-2 sm:mt-0">
							<div>
								Account number ending in
								<span class="text-ihh-light-blue">{{ selectedAccount?.last4 }}</span>
							</div>
							<div>
								Routing Number:
								<span class="text-ihh-light-blue">{{ selectedAccount?.routingNumber }}</span>
							</div>
						</div>
					</div>
					<div v-else class="flex items-center space-x-2 cursor-pointer min-h-8">
						<div>
							<svg height="25" viewBox="0 0 35 25" width="35" xmlns="http://www.w3.org/2000/svg">
								<path
									clip-rule="evenodd"
									d="M33 2H2L2 23H33V2ZM2 0C0.895431 0 0 0.89543 0 2V23C0 24.1046 0.89543 25 2 25H33C34.1046 25 35 24.1046 35 23V2C35 0.89543 34.1046 0 33 0H2Z"
									fill="#D1D7E0"
									fill-rule="evenodd"
								></path>
								<path
									clip-rule="evenodd"
									d="M11 12C11 11.4477 11.4477 11 12 11H24C24.5523 11 25 11.4477 25 12V14C25 14.5523 24.5523 15 24 15H12C11.4477 15 11 14.5523 11 14V12Z"
									fill="#D1D7E0"
									fill-rule="evenodd"
								></path>
								<path
									clip-rule="evenodd"
									d="M19 6C19.5523 6 20 6.44772 20 7V19C20 19.5523 19.5523 20 19 20H17C16.4477 20 16 19.5523 16 19V7C16 6.44772 16.4477 6 17 6H19Z"
									fill="#D1D7E0"
									fill-rule="evenodd"
								></path>
							</svg>
						</div>
						<div class="text-ihh-light-blue font-normal">Use a different account</div>
					</div>
					<div>
						<svg class="" height="24" viewBox="0 0 48 48" width="24" xmlns="http://www.w3.org/2000/svg">
							<path d="M14 20l10 10 10-10z" />
							<path d="M0 0h48v48h-48z" fill="none" />
						</svg>
					</div>
				</div>
			</div>

			<!-- ACH Account dropdown -->
			<div v-if="showACHDropdown" class="absolute z-10 w-full bg-white border rounded rounded-t-none shadow-lg !overflow-y-auto max-h-60 text-xs text-ihh-dark-gray">
				<div
					@click.stop="selectACH(account)"
					v-for="(account, index) in accounts"
					:key="index"
					class="flex items-center space-x-2 p-2 hover:bg-gray-100 cursor-pointer border min-h-16 hover:cursor-pointer hover:shadow-inner"
				>
					<div>
						<img :src="account.imageUrl" class="w-10 h-auto" />
					</div>
					<div class="flex flex-col">
						<div class="text-xs font-medium">
							Account number ending in
							<span class="text-ihh-light-blue">{{ account?.last4 }}</span>
						</div>
						<div class="text-xs font-medium">
							Routing Number:
							<span class="text-ihh-light-blue">{{ account?.routingNumber }}</span>
						</div>
					</div>
				</div>
				<div
					@click.stop="((addingNewAccount = true), (showACHDropdown = false))"
					class="flex items-center space-x-2 p-2 hover:bg-gray-100 cursor-pointer border border-t-0 min-h-16"
				>
					<div>
						<svg height="25" viewBox="0 0 35 25" width="35" xmlns="http://www.w3.org/2000/svg">
							<path
								clip-rule="evenodd"
								d="M33 2H2L2 23H33V2ZM2 0C0.895431 0 0 0.89543 0 2V23C0 24.1046 0.89543 25 2 25H33C34.1046 25 35 24.1046 35 23V2C35 0.89543 34.1046 0 33 0H2Z"
								fill="#D1D7E0"
								fill-rule="evenodd"
							></path>
							<path
								clip-rule="evenodd"
								d="M11 12C11 11.4477 11.4477 11 12 11H24C24.5523 11 25 11.4477 25 12V14C25 14.5523 24.5523 15 24 15H12C11.4477 15 11 14.5523 11 14V12Z"
								fill="#D1D7E0"
								fill-rule="evenodd"
							></path>
							<path
								clip-rule="evenodd"
								d="M19 6C19.5523 6 20 6.44772 20 7V19C20 19.5523 19.5523 20 19 20H17C16.4477 20 16 19.5523 16 19V7C16 6.44772 16.4477 6 17 6H19Z"
								fill="#D1D7E0"
								fill-rule="evenodd"
							></path>
						</svg>
					</div>
					<div class="text-ihh-light-blue font-normal">Use a different account</div>
				</div>
			</div>

			<!-- Add new ACH Account -->
			<div v-if="addingNewAccount" class="w-full space-y-2 mt-4">
				<div class="flex flex-col font-medium text-sm text-ihh-form-label space-y-2">
					<div class="space-y-1">
						<label class="font-normal">Account Type</label>
						<select v-model="newACH.accountType" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12">
							<option value="checking">Checking</option>
							<option value="savings">Savings</option>
						</select>
					</div>

					<div class="space-y-1">
						<label class="font-normal">Account Number</label>
						<input v-model="newACH.accountNumber" type="string" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12" />
					</div>

					<div class="space-y-1">
						<label class="font-normal">Routing Number</label>
						<input v-model="newACH.routingNumber" type="string" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12" />
					</div>

					<div class="space-y-1">
						<label class="font-normal">Ownership Type</label>
						<select v-model="newACH.ownershipType" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12">
							<option value="personal">Personal</option>
							<option value="business">Business</option>
						</select>
					</div>

					<div v-if="newACH.ownershipType === 'personal'" class="flex flex-col sm:flex-row sm:justify-between space-y-2 sm:space-y-0 sm:space-x-2">
						<div class="space-y-1 w-full">
							<label class="font-normal">First Name</label>
							<input v-model="newACH.firstName" type="text" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12" />
						</div>
						<div class="space-y-1 w-full">
							<label class="font-normal">Last Name</label>
							<input v-model="newACH.lastName" type="text" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12" />
						</div>
					</div>

					<div v-else>
						<div class="space-y-1 w-full">
							<label>Business Name</label>
							<input v-model="newACH.businessName" type="text" class="block w-full pl-3 pr-10 py-2 border rounded-md h-12" />
						</div>
					</div>
				</div>

				<!-- Braintree API doesn't allow processing an ACH payment without vaulting it first. -->
			</div>
		</div>
	</div>

	<!-- Manage ACH Accounts Modal -->
	<Modal maxWidth="max-w-3xl" :active="manageACH" @hide="manageACH = false">
		<template v-slot>
			<h2 class="text-xl font-semibold mb-4">Manage stored payment methods</h2>
			<table class="w-full table-auto border rounded-lg overflow-hidden">
				<thead>
					<tr class="bg-gray-100 text-left">
						<th class="p-3 w-1/4">Payment Method</th>
						<th class="p-3 w-1/4">Account Type</th>
						<th class="p-3 w-1/4">Ending in</th>
						<th class="p-3 w-1/4">Routing Number</th>
						<th class="p-3 w-1/4"></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(account, index) in accounts" :key="index" class="border-b">
						<td class="flex items-center p-3">
							<span class="mr-4">
								<img :src="account.imageUrl" class="w-10 h-auto" />
							</span>
							ACH
						</td>
						<td class="p-3 text-ihh-light-blue">{{ account.ownershipType === 'personal' ? 'Personal' : 'Business' }}</td>
						<td class="p-3 text-ihh-light-blue">{{ account.last4 }}</td>
						<td class="p-3 text-ihh-light-blue">{{ account.routingNumber }}</td>
						<td class="p-3 text-right">
							<button
								@click="deleteACHAccount(account.token)"
								class="border border-ihh-ccform-gray rounded text-ihh-form-label text-sm px-4 py-1 hover:bg-red-400 hover:text-white hover:border-red-400"
							>
								Delete
							</button>
						</td>
					</tr>
				</tbody>
			</table>
		</template>
	</Modal>
</template>

<script>
import axios from 'axios'
import Braintree from '../../helpers/braintree.js'
import { debounce } from 'lodash'
import Swal from 'sweetalert2'

export default {
	name: 'ACH',
	inject: ['store', 'checkout'],
	data() {
		return {
			// UI Variables
			showACHDropdown: false,
			addingNewAccount: false,
			manageACH: false,

			// ACH Variables
			accounts: [],
			selectedAccount: null,
			newACH: {
				accountType: 'checking',
				accountNumber: '',
				routingNumber: '',
				ownershipType: 'personal',
				firstName: '',
				lastName: '',
				businessName: '',
			},
		}
	},

	async mounted() {
		await this.fetchAccounts()
	},

	methods: {
		async fetchAccounts() {
			if (!this.store?.user?.braintreeId) {
				console.log('No braintree user account found: skipping fetch vaulted payment')
				this.addingNewAccount = true
				return
			}

			console.log('Fetching Braintree vaulted payment methods for user: ', this.store.user.bcId)

			try {
				let response = await axios.post(`${this.store.api}/controllers/braintree/vault`, { customerId: this.store?.user?.braintreeId })

				if (response?.data?.success) {
					if (!response?.data?.paymentMethods?.length) {
						this.addingNewAccount = true
						this.checkout.updateAddingNewACH(true)
						return
					}
					console.log('Success fetching vaulted payment methods...')
					const vaultedMethods = response.data.paymentMethods.filter((method) => method?.bankName)

					this.accounts = vaultedMethods.map((account) => ({
						routingNumber: account.routingNumber,
						last4: account.last4,
						accountHolderName: account.accountHolderName,
						bankName: account.bankName,
						ownershipType: account.ownershipType,
						firstName: account.firstName,
						lastName: account.lastName,
						default: account.default,
						token: account.token,
						customerId: account.customerId,
						imageUrl: account.imageUrl,
						verifications: account.verifications,
					}))
					this.selectedAccount = this.accounts[0]
				} else {
					console.log('Error fetching stored payment methods...')
				}
			} catch (error) {
				console.log('Error fetching Braintree payment methods:', error)
			}
		},
		async deleteACHAccount(token) {
			console.log('Deleting ACH account with token: ', token)
			const response = await Braintree.deleteACHPaymentMethod(token, this.store.api)
			if (response?.success) {
				this.accounts = this.accounts.filter((account) => account.token !== token)
				this.selectedAccount = this.accounts[0]
				return Swal.fire({
					toast: true,
					position: 'center',
					icon: 'success',
					title: 'Payment method deleted successfully',
					showConfirmButton: false,
					timer: 3000,
					timerProgressBar: true,
				})
			}
			return Swal.fire({
				icon: 'error',
				title: 'Error deleting ACH account',
				text: 'Please try again or contact support.',
			})
		},
		toggleACHDropdown() {
			this.showACHDropdown = !this.showACHDropdown
		},
		selectACH(account) {
			this.selectedAccount = account
			this.showACHDropdown = false
			this.addingNewAccount = false
		},
	},

	watch: {
		newACH: {
			handler: debounce(function (newVal) {
				this.checkout.setNewACH(newVal)
			}, 500),
			deep: true,
		},
		selectedAccount: debounce(function (newVal) {
			this.checkout.selectACH(newVal)
		}, 300),
		addingNewAccount: debounce(function (newVal) {
			this.checkout.updateAddingNewACH(newVal)
		}, 300),
	},
}
</script>

<style scoped></style>
